import React, { type ComponentType, type FC, type ReactNode, useMemo } from 'react'
import { Divider, type IconButtonProps, type SvgIconProps, type SxProps, ThemeProvider, Typography, useTheme } from '@mui/material'
import type { JSONSchema7, JSONSchema7Definition, JSONSchema7TypeName } from 'json-schema'
import {
    type ArrayFieldTitleProps,
    type DescriptionFieldProps,
    type IconButtonProps as RJSFIconButtonProps,
    type RJSFSchema,
    TranslatableString,
} from '@rjsf/utils'
import { type FormProps, type ThemeProps, withTheme } from '@rjsf/core'
import { Templates, Theme } from '@rjsf/mui'
import { customizeValidator } from '@rjsf/validator-ajv8'

import {
    assert,
    copyObjectWithoutKeys,
    isArray,
    isNonArrayObject,
    isObjectEmpty,
    nameFunction,
    objectEntries,
    type PartialReadonlyRecord,
    type ReadonlyRecord,
} from '../../../../utils'

import { ConnectwareError, ConnectwareErrorType, type CybusServiceRawParameters, isSecretProperty } from '../../../../domain'

import { AddIcon, ArrowDownIcon, ArrowUpIcon, IconButton, MinusIcon } from '../../common'

/**
 * Some components use icons inside them
 * They are supposed to use our custom icons instead
 *
 * But since they are not actually used, there is no need to implement them
 * So this unmistakable warning system is here instead
 */
export const createNonImplementedFormComponent = (componentName: string): FC<ReadonlyRecord<never, never>> =>
    nameFunction(() => {
        throw new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'Form component is not expected to be in use', { componentName })
    }, componentName)

const createButtonFormComponent = (
    label: TranslatableString,
    Icon: FC<SvgIconProps>,
    variant: IconButtonProps['variant']
): ComponentType<RJSFIconButtonProps<unknown, RJSFSchema, ReadonlyRecord<never, never>>> =>
({ uiSchema, registry, ...props }) => (
    <IconButton {...props} title={registry.translateString(label)} variant={variant}>
        <Icon fontSize="small" />
    </IconButton>
)

const arrayFieldTitleTemplateDividerStyle: SxProps = { mb: 2 }

const formTheme: ThemeProps = {
    ...Theme,
    templates: {
        ...Templates,
        ButtonTemplates: {
            ...Templates.ButtonTemplates,
            /** Hides the submit button (and only the submit button) */
            SubmitButton: () => null,
            AddButton: createButtonFormComponent(TranslatableString.AddItemButton, AddIcon, 'ghost'),
            RemoveButton: createButtonFormComponent(TranslatableString.RemoveButton, MinusIcon, 'dangerGhost'),
            MoveDownButton: createButtonFormComponent(TranslatableString.MoveDownButton, ArrowDownIcon, 'ghost'),
            MoveUpButton: createButtonFormComponent(TranslatableString.MoveUpButton, ArrowUpIcon, 'ghost'),
            CopyButton: createNonImplementedFormComponent('CopyButton'),
            ClearButton: createNonImplementedFormComponent('ClearButton'),
        },
        ErrorListTemplate: createNonImplementedFormComponent('ErrorListTemplate'),
        OptionalDataControlsTemplate: createNonImplementedFormComponent('OptionalDataControlsTemplate'),

        DescriptionFieldTemplate: ({ description }: DescriptionFieldProps) => <Typography variant="body2">{description}</Typography>,
        ArrayFieldTitleTemplate: ({ title }: ArrayFieldTitleProps) => (
            <>
                <Typography variant="h6">{title}</Typography>
                <Divider sx={arrayFieldTitleTemplateDividerStyle} />
            </>
        ),
    },
}

export type FormData = CybusServiceRawParameters & PartialReadonlyRecord<'id', string>

type Props<F extends FormData> =
    & Omit<FormProps<F>, 'validator' | 'onChange' | 'showErrorList' | 'liveValidate'>
    & Readonly<{ emptyForm: ReactNode, onChange: (form: F) => void }>

const primitiveRemaps = new Set<JSONSchema7TypeName>(['string', 'number', 'integer', 'boolean'])

/**
 * When a @rjsf form has one of its fields emptied, it falls back to use the default
 * That is, if someone removes all the characters in a field, the default value will pop in its place
 *
 * This function avoids the odd behaviour by, when emptied, marking the field as empty
 *
 * @see {primitiveRemaps}
 *
 * A `secret: true` property always has its default stripped too, regardless of type: a schema
 * default is never a value the user typed, and rjsf fills it into formData for any field missing
 * from the (already secret-stripped) parameters — see `getSecretParameterNames`
 */
const simplifyDefaults = (schema: JSONSchema7Definition): JSONSchema7 => {
    let out = schema

    /** The schemas provided by the backend don´t come as boolean, so just ignore it */
    assert(typeof out !== 'boolean', new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'Unexpected schema loaded in parameters form'))

    /** Step into object properties recursively */
    if (out.properties) {
        out = {
            ...out,
            properties: Object.fromEntries(objectEntries(out.properties).map(([name, value]) => [name, simplifyDefaults(value)] as const)),
        }
    }

    /** Step into array items recursively */
    if (isNonArrayObject(out.items)) {
        out = { ...out, items: simplifyDefaults(out.items) }
    } else if (isArray(out.items)) {
        out = { ...out, items: out.items.map((v) => simplifyDefaults(v)) }
    }

    if (isSecretProperty(out) || (typeof out.type === 'string' && primitiveRemaps.has(out.type))) {
        /** Replace the value with absence */
        out = copyObjectWithoutKeys(out, 'default')
    }

    return out
}

/**
 * The standard form (from '@rjsf/mui') comes with UI choices that do not match with the connectware
 * This component re-aligns it with the least amount of code to maintain
 */
export const Form = <F extends FormData>({ emptyForm, schema, onChange, ...props }: Props<F>): ReturnType<FC> => {
    const theme = useTheme()
    const ThemedForm = useMemo(() => withTheme<F>(formTheme), [])
    const validator = useMemo(() => customizeValidator<F>(), [])
    const simplifiedSchema = useMemo(() => simplifyDefaults(schema), [schema])

    if (!simplifiedSchema.properties || isObjectEmpty(simplifiedSchema.properties)) {
        /** No properties? Just render the empty notice */
        return emptyForm
    }

    return (
        <ThemeProvider
            theme={{
                ...theme,
                components: {
                    ...theme.components,
                    /**
                     * Forces text fields to follow standard styling
                     * TODO Remove these components overrides,
                     * TODO RJSF form should use input fields defined in our theme.
                     * @see https://cybusio.atlassian.net/browse/CC-4594
                     */
                    MuiTextField: { defaultProps: { variant: 'standard' } },
                },
            }}
        >
            <ThemedForm
                {...props}
                schema={simplifiedSchema}
                validator={validator}
                showErrorList={false}
                liveValidate
                onChange={({ formData }) => {
                    /**
                     * In @rjsf v6 the formData prop is marked as non-mandatory through its whole type system.
                     * We never pass an undefined value, so no undefined value should be expected
                     */
                    assert(formData !== undefined, new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'Form is not expected to be undefined'))

                    onChange(formData)
                }}
            />
        </ThemeProvider>
    )
}
